/* eslint-disable react/no-unused-prop-types */

import React, { forwardRef, type JSX } from 'react';
import styled, { css } from 'styled-components';
import { type Target } from '@/app/components/search/components/Form';
import { focusRing } from '@/app/components/search/styles/mixins.ts';
import { IconGlyph } from '@/app/common/icons/MaskIcon';

const MealPlanContainer = styled.div`
  width: 100%;
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
`;

const Label = styled.label<{ isSelected: boolean; disabled: boolean }>`
  position: relative;
  width: calc(100% / 3);
  display: flex;
  flex-direction: column;
  align-items: center;
  cursor: pointer;
  font-size: ${({ theme }) => theme.kallisto.font.size['150']};
  margin-top: 8px;
  margin-bottom: 8px;

  &:has(input:focus-visible) {
    border-radius: 2px;
    ${focusRing}
  }

  ${IconGlyph} {
    color: ${({ theme }) => theme.kallisto.color.text.static.textPrimary};
  }

  ${({ isSelected }) =>
    isSelected &&
    css`
      div:first-of-type {
        background: ${({ theme }) => theme.kallisto.color.background.interactive.backgroundPrimarySelected};
        border-color: transparent;
      }
      &:hover {
        div:first-of-type {
          border-color: ${({ theme }) => theme.kallisto.color.border.interactive.borderPrimaryDefault};
        }
      }
      ${IconGlyph} {
        color: ${({ theme }) => theme.kallisto.color.background.static.backgroundBrandPrimary};
      }
    `}

  ${({ disabled }) =>
    disabled
      ? css`
          color: ${({ theme }) => theme.kallisto.color.common.black[200]};
          cursor: default;

          div:first-of-type {
            background-color: ${({ theme }) => theme.kallisto.color.common.black[50]};
            border-color: transparent !important;
          }
          div:last-of-type {
            color: ${({ theme }) => theme.kallisto.color.common.black[200]} !important;
          }
          ${IconGlyph} {
            color: ${({ theme }) => theme.kallisto.color.common.black[200]};
          }
        `
      : css`
          &:hover {
            div:first-of-type {
              border-color: ${({ theme }) => theme.kallisto.color.border.interactive.borderPrimaryDefault};
            }
            ${IconGlyph} {
              color: ${({ theme }) => theme.kallisto.color.background.static.backgroundBrandPrimary};
            }
          }
        `}
`;

type MealPlanProps = {
  value: number[];
  name: string;
  options: {
    value: number;
    label: JSX.Element;
    disabled: boolean;
  }[];
  onUpdate: (e: Target) => Promise<void>;
};

const MealPlan = ({ options, value, name, onUpdate }: MealPlanProps) => {
  const handleItemClick = (val: number, disabled: boolean) => {
    if (!disabled) {
      if (val !== 0) {
        if (value.indexOf(val) !== -1) {
          onUpdate({ target: { name, value: value.filter((o: number) => o !== val) } });
        } else {
          value.push(val);
          onUpdate({ target: { name, value } });
        }
      }
      if (val === 0) {
        onUpdate({ target: { name, value: [] } });
      }
    }
  };

  const handleItemKeyDown = (e: React.KeyboardEvent<HTMLInputElement>, val: number, disabled: boolean) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      e.stopPropagation();
      handleItemClick(val, disabled);
    }
  };

  return (
    <MealPlanContainer>
      {options.map((x, i) => {
        const isSelected = i > 0 ? value && value.indexOf(x.value) !== -1 : value && value.length === 0;
        const id = `${name}_${x.value}`;

        return (
          <Label key={id} htmlFor={id} isSelected={isSelected} disabled={x.disabled}>
            <input
              id={id}
              type="checkbox"
              name={name}
              checked={isSelected}
              disabled={x.disabled}
              onChange={() => handleItemClick(x.value, x.disabled)}
              onKeyDown={(e) => handleItemKeyDown(e, x.value, x.disabled)}
              className="sr-only"
            />
            {x.label}
          </Label>
        );
      })}
    </MealPlanContainer>
  );
};

// eslint-disable-next-line react/display-name
export default forwardRef((props: MealPlanProps, ref) => <MealPlan {...{ innerRef: ref, ...props }} />);
